import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, XCircle, Clock, AlertCircle, Calendar, Edit } from 'lucide-react'
import { Submission, SubmissionStatus, ResponseType } from '@/types'
import { format, differenceInDays } from 'date-fns'

const statusIcons = {
  [SubmissionStatus.SUBMITTED]: Clock,
  [SubmissionStatus.UNDER_REVIEW]: AlertCircle,
  [SubmissionStatus.ACCEPTED]: CheckCircle,
  [SubmissionStatus.REJECTED]: XCircle,
  [SubmissionStatus.WITHDRAWN]: XCircle
}

const statusColors = {
  [SubmissionStatus.SUBMITTED]: 'text-blue-600 bg-blue-100',
  [SubmissionStatus.UNDER_REVIEW]: 'text-yellow-600 bg-yellow-100',
  [SubmissionStatus.ACCEPTED]: 'text-green-600 bg-green-100',
  [SubmissionStatus.REJECTED]: 'text-red-600 bg-red-100',
  [SubmissionStatus.WITHDRAWN]: 'text-gray-600 bg-gray-100'
}

const responseTypeLabels = {
  [ResponseType.ACCEPTANCE]: 'Acceptance',
  [ResponseType.REJECTION]: 'Rejection',
  [ResponseType.HOLD]: 'Hold for Further Review',
  [ResponseType.REQUEST_REVISIONS]: 'Revision Request'
}

export default function Submissions() {
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<SubmissionStatus | 'all'>('all')

  useEffect(() => {
    // TODO: Fetch submissions from API
    const mockSubmissions: Submission[] = [
      {
        id: '1',
        userId: 'user1',
        workId: '1',
        journalId: '1',
        status: SubmissionStatus.UNDER_REVIEW,
        submittedAt: '2024-01-15T10:00:00Z',
        createdAt: '2024-01-15T10:00:00Z',
        updatedAt: '2024-01-15T10:00:00Z',
        work: {
          id: '1',
          userId: 'user1',
          title: 'Ocean Waves',
          type: 'poem' as any,
          content: '',
          wordCount: 45,
          status: 'submitted' as any,
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-15T10:00:00Z'
        },
        journal: {
          id: '1',
          name: 'Poetry Magazine',
          description: '',
          acceptedTypes: [],
          isActive: true,
          createdAt: '2024-01-01T10:00:00Z',
          updatedAt: '2024-01-01T10:00:00Z'
        }
      },
      {
        id: '2',
        userId: 'user1',
        workId: '2',
        journalId: '2',
        status: SubmissionStatus.ACCEPTED,
        submittedAt: '2024-01-10T10:00:00Z',
        responseDate: '2024-02-15',
        responseType: ResponseType.ACCEPTANCE,
        notes: 'Congratulations! We are pleased to accept your work.',
        createdAt: '2024-01-10T10:00:00Z',
        updatedAt: '2024-02-15T10:00:00Z',
        work: {
          id: '2',
          userId: 'user1',
          title: 'The Last Train',
          type: 'short_story' as any,
          content: '',
          wordCount: 2500,
          status: 'published' as any,
          createdAt: '2024-01-10T10:00:00Z',
          updatedAt: '2024-01-10T10:00:00Z'
        },
        journal: {
          id: '2',
          name: 'Literary Quarterly',
          description: '',
          acceptedTypes: [],
          isActive: true,
          createdAt: '2024-01-01T10:00:00Z',
          updatedAt: '2024-01-01T10:00:00Z'
        }
      }
    ]
    setSubmissions(mockSubmissions)
    setLoading(false)
  }, [])

  const getDaysWaiting = (submittedAt: string, responseDate?: string) => {
    const startDate = new Date(submittedAt)
    const endDate = responseDate ? new Date(responseDate) : new Date()
    return differenceInDays(endDate, startDate)
  }

  const filteredSubmissions = submissions.filter(submission =>
    filter === 'all' || submission.status === filter
  )

  const activeSubmissions = submissions.filter(s => 
    s.status === SubmissionStatus.SUBMITTED || s.status === SubmissionStatus.UNDER_REVIEW
  )

  const stats = {
    total: submissions.length,
    active: activeSubmissions.length,
    accepted: submissions.filter(s => s.status === SubmissionStatus.ACCEPTED).length,
    rejected: submissions.filter(s => s.status === SubmissionStatus.REJECTED).length
  }

  if (loading) {
    return <div className="text-center py-8">Loading submissions...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">My Submissions</h1>
        <Link
          to="/works"
          className="btn btn-primary"
        >
          Submit New Work
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Submissions</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.active}</div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">{stats.accepted}</div>
          <div className="text-sm text-gray-600">Accepted</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
          <div className="text-sm text-gray-600">Rejected</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            filter === 'all'
              ? 'bg-primary-100 text-primary-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Submissions
        </button>
        {Object.values(SubmissionStatus).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-md text-sm font-medium capitalize ${
              filter === status
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Submissions List */}
      {filteredSubmissions.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by submitting your literary works to journals.
          </p>
          <div className="mt-6">
            <Link to="/works" className="btn btn-primary">
              Submit Work
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSubmissions.map((submission) => {
            const StatusIcon = statusIcons[submission.status]
            const daysWaiting = getDaysWaiting(submission.submittedAt, submission.responseDate)
            
            return (
              <div key={submission.id} className="card">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {submission.work?.title}
                      </h3>
                      <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[submission.status]}`}>
                        <StatusIcon size={14} />
                        <span className="capitalize">{submission.status.replace('_', ' ')}</span>
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 mb-3">
                      Submitted to <strong>{submission.journal?.name}</strong> on{' '}
                      {format(new Date(submission.submittedAt), 'MMM d, yyyy')}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Days Waiting:</span>
                        <span className="ml-2 text-gray-600">{daysWaiting}</span>
                      </div>
                      
                      <div>
                        <span className="font-medium text-gray-700">Word Count:</span>
                        <span className="ml-2 text-gray-600">{submission.work?.wordCount}</span>
                      </div>

                      {submission.responseDate && (
                        <div>
                          <span className="font-medium text-gray-700">Response:</span>
                          <span className="ml-2 text-gray-600">
                            {format(new Date(submission.responseDate), 'MMM d, yyyy')}
                          </span>
                        </div>
                      )}
                    </div>

                    {submission.responseType && (
                      <div className="mt-3 text-sm">
                        <span className="font-medium text-gray-700">Response Type:</span>
                        <span className="ml-2 text-gray-600">
                          {responseTypeLabels[submission.responseType]}
                        </span>
                      </div>
                    )}

                    {submission.notes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <div className="text-sm font-medium text-gray-700 mb-1">Notes:</div>
                        <div className="text-sm text-gray-600">{submission.notes}</div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      className="text-gray-600 hover:text-gray-900"
                      title="Edit submission"
                    >
                      <Edit size={18} />
                    </button>
                    
                    {submission.followUpDate && (
                      <div className="flex items-center text-sm text-orange-600">
                        <Calendar size={16} className="mr-1" />
                        Follow up: {format(new Date(submission.followUpDate), 'MMM d')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}